import { cutToast } from 'utility/toast';
import { IconButton, Pivot, PivotItem, Stack } from '@fluentui/react';
import React, { useCallback } from 'react';
import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GetPayCycleDetails } from '../__generated__/GetPayCycleDetails';
import { PayCycleCommonTypes_secureRowLevels } from 'ap/paymentCycle/__generated__/PayCycleCommonTypes';
import { useStyles } from './index.styles';
import { PivotCustomRender } from 'common/components/PivotComponent';
import { PivotDisplay } from 'common/components/PivotDisplay';
import {
  ListPayCycleNotes,
  ListPayCycleNotesVariables,
} from './PayCycleNotes/__generated__/ListPayCycleNotes';
import { HistoryActionMenuView } from 'common/components/History';
import { ProtectSection } from 'common/components/Protect';

import {
  PayCycleRowProtection,
  PayCycleRowProtectionVariables,
} from './__generated__/PayCycleRowProtection';
import { PayCycleNotesSection } from './PayCycleNotes';
import { AttachSection } from './PayCycleAttach';
import { PayCycleUploadDocument_payCycleUploadDocument } from 'ap/paymentCycle/__generated__/PayCycleUploadDocument';
import ENTITY_NOTES_LIST from './PayCycleNotes/ListPayCycleNotes.graphql';
import PAYCYCLE_ROW_PROTECTION from './PayCycleRowProtection.graphql';
import INVOICE_DETAILS from '../GetPayCycleDetails.graphql';
const ActionConsts = {
  protect: 'Protect_Action',
  notes: 'Notes_Action',
  history: 'History_Action',
  attach: 'Attach_Action',
};
interface ActionMenuProps {
  payCycleId: string;
  payCycleDetails: GetPayCycleDetails | undefined;
  secureRowLevels: PayCycleCommonTypes_secureRowLevels | undefined | null;
  onUpload?: (
    fileSelected: File,
    documentData: PayCycleUploadDocument_payCycleUploadDocument,
    toastId: string
  ) => void;
}
export const ActionsMenu: React.FC<ActionMenuProps> = ({
  payCycleId,
  payCycleDetails,
  secureRowLevels,
  onUpload,
}) => {
  const [selectedKey, setSelectedKey] = React.useState(ActionConsts.protect);
  const [expandMenu, setExpandMenu] = useState(false);
  const styles = useStyles();

  const { data: entityNotesData, refetch: entityNotesListRefetch } = useQuery<
    ListPayCycleNotes,
    ListPayCycleNotesVariables
  >(ENTITY_NOTES_LIST, {
    variables: {
      id: payCycleId!,
    },
  });
  const getEntityNotes = () => {
    const variables: ListPayCycleNotesVariables = {
      id: payCycleId!,
    };
    entityNotesListRefetch(variables);
  };
  const getEntityNotesMemo = useCallback(getEntityNotes, [payCycleId]);

  const [updateRowProtection, { loading: updateLoading, error: updateError }] =
    useMutation<PayCycleRowProtection, PayCycleRowProtectionVariables>(
      PAYCYCLE_ROW_PROTECTION
    );

  const updateRowLevel = async (selectedLevel: string | null) => {
    const inputData =
      selectedLevel !== null
        ? {
            entityId: payCycleDetails?.payCycle?.id!,
            rowSecurityId: selectedLevel,
          }
        : {
            entityId: payCycleDetails?.payCycle?.id!,
            isProtectionRemoval: true,
          };
    await updateRowProtection({
      variables: {
        input: inputData,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: INVOICE_DETAILS,
          variables: {
            id: payCycleDetails?.payCycle?.id,
          },
        },
      ],
    });
    if (!updateError) {
      cutToast.success('Security/Protection level upgraded');
    } else cutToast.error('Failed to upgrade protection level');
  };

  const getSelectedSection = (key: string) => {
    switch (key) {
      case 'Protect_Action':
        return (
          <ProtectSection
            secureRowLevels={secureRowLevels?.nodes!}
            secureRowLevelId={payCycleDetails?.payCycle?.secureRowLevel?.id}
            updateLoading={updateLoading}
            updateRowLevel={(selectedLevel) => {
              updateRowLevel(selectedLevel);
            }}
          />
        );
      case 'Notes_Action':
        return (
          <PayCycleNotesSection
            payCycleId={payCycleDetails?.payCycle?.id!}
            data={entityNotesData?.payCycle?.notesByEntityId.nodes!}
            listRefreshRequest={getEntityNotesMemo}
          />
        );

      case 'History_Action':
        return (
          <HistoryActionMenuView
            moduleName="Payment Cycle"
            numberOfItems={4}
            historyData={
              payCycleDetails?.payCycle?.payCycleHistoriesByEntityId.nodes || []
            }
          />
        );
      case 'Attach_Action':
        return (
          <AttachSection
            secureRowLevel={secureRowLevels?.nodes!}
            paymentCycle={payCycleDetails?.payCycle!}
            onUpload={onUpload}
          />
        );
      default:
        return null;
    }
  };
  return (
    <Stack>
      {expandMenu ? (
        <Stack>
          <Stack horizontalAlign="space-between" horizontal>
            <Stack horizontal className={styles.centerAlign}>
              <Pivot
                selectedKey={selectedKey}
                onLinkClick={(item: PivotItem | undefined) => {
                  setSelectedKey(item?.props.itemKey || ActionConsts.protect);
                }}
                aria-label="Menu_Actions"
              >
                <PivotItem
                  itemKey={ActionConsts.protect}
                  itemIcon={
                    payCycleDetails?.payCycle?._isProtected ? 'Lock' : 'Unlock'
                  }
                  headerText="Protect"
                  itemCount={0}
                  onRenderItemLink={PivotCustomRender}
                />
                <PivotItem
                  itemKey={ActionConsts.attach}
                  itemIcon="Attach"
                  headerText="Files"
                  itemCount={
                    payCycleDetails?.payCycle?.entityDocumentsByEntityId?.nodes
                      ?.length
                  }
                  onRenderItemLink={PivotCustomRender}
                />
                <PivotItem
                  itemKey={ActionConsts.notes}
                  itemIcon="Page"
                  headerText="Notes"
                  itemCount={
                    entityNotesData?.payCycle?.notesByEntityId?.nodes?.length
                  }
                  onRenderItemLink={PivotCustomRender}
                />
                <PivotItem
                  itemKey={ActionConsts.history}
                  itemIcon="History"
                  headerText="History"
                  itemCount={
                    payCycleDetails?.payCycle?.payCycleHistoriesByEntityId.nodes
                      .length
                  }
                  onRenderItemLink={PivotCustomRender}
                />
              </Pivot>
              <IconButton
                onClick={() => setExpandMenu(false)}
                iconProps={{ iconName: 'ChevronUp' }}
                className={styles.iconButtonColor}
                ariaLabel="Collapse"
              />
            </Stack>
          </Stack>

          <Stack>{getSelectedSection(selectedKey)}</Stack>
        </Stack>
      ) : (
        <Stack horizontalAlign="space-between" horizontal>
          <Stack horizontal verticalAlign="center">
            <Stack horizontal verticalAlign="center">
              <PivotDisplay
                name="Protect"
                iconName={
                  payCycleDetails?.payCycle?._isProtected ? 'Lock' : 'Unlock'
                }
                onPivotClick={() => {
                  setSelectedKey(ActionConsts.protect);
                  setExpandMenu(true);
                }}
              />
              <PivotDisplay
                name="Files"
                iconName="Attach"
                count={
                  payCycleDetails?.payCycle?.entityDocumentsByEntityId?.nodes
                    ?.length
                }
                onPivotClick={() => {
                  setSelectedKey(ActionConsts.attach);
                  setExpandMenu(true);
                }}
              />
              <PivotDisplay
                name="Notes"
                iconName="Page"
                count={entityNotesData?.payCycle?.notesByEntityId.nodes.length}
                onPivotClick={() => {
                  setSelectedKey(ActionConsts.notes);
                  setExpandMenu(true);
                }}
              />
              <PivotDisplay
                name="History"
                iconName="History"
                count={
                  payCycleDetails?.payCycle?.payCycleHistoriesByEntityId.nodes
                    .length
                }
                onPivotClick={() => {
                  setSelectedKey(ActionConsts.history);
                  setExpandMenu(true);
                }}
              />
            </Stack>
            <IconButton
              onClick={() => setExpandMenu(true)}
              iconProps={{ iconName: 'ChevronDown' }}
              className={styles.iconButtonColor}
              ariaLabel="Expand"
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
